import { useEffect } from 'react';
import { IntersectionObserverProps, useInView } from 'react-intersection-observer';

interface LazySectionProps {
  children?: React.ReactNode;
  delay?: number;
  setIsDependencyInView?: (isInView: boolean) => void;
  useInViewProps?: Partial<IntersectionObserverProps>;
}

const LazySectionWithEffect = ({
  delay = 1000,
  setIsDependencyInView,
  useInViewProps = {},
  children,
}: LazySectionProps): JSX.Element => {
  const { ref: sectionRef, inView: isSectionInView } = useInView({
    ...useInViewProps,
    triggerOnce: true,
  });

  useEffect(() => {
    if (isSectionInView) {
      /**
       * Required for contiguous sections. As they start as empty divs both would be intersecting
       * at the same time. Delay the rendering of the dependant section to avoid this.
       */
      const timer = setTimeout(() => setIsDependencyInView(true), delay);
      return () => clearTimeout(timer);
    }
  }, [delay, isSectionInView, setIsDependencyInView]);

  return isSectionInView ? <>{children}</> : <div ref={sectionRef} />;
};

const LazySectionWithoutEffect = ({
  useInViewProps = {},
  children,
}: Omit<LazySectionProps, 'setIsDependencyInView'>): JSX.Element => {
  const { ref: sectionRef, inView: isSectionInView } = useInView({
    ...useInViewProps,
    triggerOnce: true,
  });

  return isSectionInView ? <>{children}</> : <div ref={sectionRef} />;
};

const LazySection = ({
  isDependencyInView,
  ...props
}: { isDependencyInView?: boolean } & LazySectionProps) => {
  if (isDependencyInView !== undefined && !isDependencyInView) return null;

  const Component = props.setIsDependencyInView ? LazySectionWithEffect : LazySectionWithoutEffect;
  return <Component {...props} />;
};

export default LazySection;
